import { Link } from "react-router-dom";
import {
  Eye,
  Send,
  Trash2,
  Loader2,
  CheckCircle2,
  XCircle,
  CalendarX,
  Clock,
  FileEdit,
  BarChart3,
  RotateCcw,
  MessageCircle,
} from "lucide-react";
import { fmtTime } from "@/components/admin/AnalyticsModal";

export function isQuoteExpired(q) {
  if (q.status !== "sent" || q.expiry_type === "no_expiry") return false;
  return ukDaysLeft(q) < 0;
}

const ukDayNum = (d) => {
  const [dd, mm, yy] = d
    .toLocaleDateString("en-GB", { timeZone: "Europe/London" })
    .split("/")
    .map(Number);
  return new Date(yy, mm - 1, dd);
};

export function ukDaysLeft(q) {
  const base = q.hold_expires_at
    ? new Date(q.hold_expires_at)
    : new Date(new Date(q.created_at).getTime() + 7 * 86400000);
  return Math.round((ukDayNum(base) - ukDayNum(new Date())) / 86400000);
}

function ExpiryLabel({ q }) {
  if (q.status !== "sent") return null;
  if (q.expiry_type === "no_expiry") {
    return (
      <div
        data-testid={`expiry-label-${q.id}`}
        className="mt-1.5 text-[10px] tracking-overline uppercase font-semibold text-[#8A8782]"
      >
        No expiry
      </div>
    );
  }
  const days = ukDaysLeft(q);
  if (days < 0) return null;
  const text = days === 0 ? "Expires today" : days === 1 ? "Expires tomorrow" : `Expires in ${days} days`;
  const urgent = days <= 2;
  return (
    <div
      data-testid={`expiry-label-${q.id}`}
      className={`mt-1.5 text-[10px] tracking-overline uppercase font-semibold ${
        urgent ? "text-[#B3261E]" : "text-[#8A8782]"
      }`}
    >
      {text}
    </div>
  );
}

export function StatusBadge({ status }) {
  const map = {
    draft: {
      label: "Draft",
      cls: "bg-[#F3F1ED] text-[#5C5A56] border border-[#D1CEC2]",
      Icon: FileEdit,
    },
    sent: {
      label: "Sent",
      cls: "bg-[#E9EDE9] text-[#3E5743] border border-[#3E5743]/30",
      Icon: Clock,
    },
    accepted: {
      label: "Accepted",
      cls: "bg-[#2F2E2E] text-white border border-[#2F2E2E]",
      Icon: CheckCircle2,
    },
    declined: {
      label: "Declined",
      cls: "bg-[#FBEAEA] text-[#B3261E] border border-[#B3261E]/40",
      Icon: XCircle,
    },
    expired: {
      label: "Expired",
      cls: "bg-[#F7F0DC] text-[#8A6D1A] border border-[#8A6D1A]/40",
      Icon: CalendarX,
    },
  };
  const s = map[status] || map.draft;
  const Icon = s.Icon;
  return (
    <span
      data-testid={`status-badge-${status}`}
      className={`inline-flex items-center gap-1.5 px-3 py-1 text-[10px] tracking-overline uppercase font-medium ${s.cls}`}
    >
      <Icon className="w-3 h-3" strokeWidth={1.5} />
      {s.label}
    </span>
  );
}

function EngagementCell({ q, stats, onAnalytics }) {
  if (!stats) return <span className="text-xs text-[#8A8782]">Not viewed yet</span>;
  return (
    <button
      onClick={() => onAnalytics(q)}
      className="text-left hover:opacity-70 transition-opacity"
      title="View analytics"
    >
      <div className="text-sm text-[#1F1E1D] tabular-nums">
        {stats.live && (
          <span
            data-testid={`live-dot-${q.id}`}
            title="Viewing right now"
            className="inline-flex items-center gap-1.5 mr-2 text-[10px] tracking-overline uppercase font-medium text-[#2E7D32]"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#2E7D32] opacity-60" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#2E7D32]" />
            </span>
            Live
          </span>
        )}
        {stats.views} visit{stats.views === 1 ? "" : "s"} · {fmtTime(stats.total_seconds)}
        {stats.devices > 0 && (
          <span className="text-[#A9927D]"> · {stats.devices} device{stats.devices === 1 ? "" : "s"}</span>
        )}
      </div>
      {stats.last_viewed && (
        <div className="text-xs text-[#8A8782] mt-0.5">
          Last:{" "}
          {new Date(stats.last_viewed).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
          })}
        </div>
      )}
    </button>
  );
}

function RowActions({ q, sendingId, onSend, onDelete, onAnalytics, onReactivate, onWhatsApp }) {
  return (
    <div className="flex items-center gap-2 justify-end">
      <button
        onClick={() => onWhatsApp(q)}
        data-testid={`whatsapp-btn-${q.id}`}
        className="inline-flex items-center gap-1.5 text-[#25D366] hover:text-[#1DA851] px-3 py-2 text-[11px] tracking-overline uppercase font-medium transition-colors"
        title="WhatsApp them (pulls mobile from Brevo)"
      >
        <MessageCircle className="w-4 h-4" strokeWidth={1.5} />
      </button>
      <button
        onClick={() => onReactivate(q)}
        data-testid={`reactivate-btn-${q.id}`}
        className={`inline-flex items-center gap-1.5 px-3 py-2 text-[11px] tracking-overline uppercase font-medium transition-colors ${
          q.reactivation_requested
            ? "text-[#CCBA56] hover:text-[#B5A244]"
            : "text-[#5C5A56] hover:text-[#1F1E1D]"
        }`}
        title="Reactivate (restart 7-day hold)"
      >
        <RotateCcw className="w-4 h-4" strokeWidth={1.5} />
      </button>
      <button
        onClick={() => onAnalytics(q)}
        data-testid={`analytics-btn-${q.id}`}
        className="inline-flex items-center gap-1.5 text-[#5C5A56] hover:text-[#1F1E1D] px-3 py-2 text-[11px] tracking-overline uppercase font-medium transition-colors"
        title="Analytics"
      >
        <BarChart3 className="w-4 h-4" strokeWidth={1.5} />
      </button>
      <Link
        to={`/q/${q.slug}?preview=1`}
        target="_blank"
        data-testid={`preview-btn-${q.id}`}
        className="inline-flex items-center gap-1.5 text-[#5C5A56] hover:text-[#1F1E1D] px-3 py-2 text-[11px] tracking-overline uppercase font-medium transition-colors"
        title="Preview"
      >
        <Eye className="w-4 h-4" strokeWidth={1.5} />
      </Link>
      <Link
        to={`/admin/quotes/${q.id}`}
        data-testid={`edit-btn-${q.id}`}
        className="inline-flex items-center gap-1.5 text-[#5C5A56] hover:text-[#1F1E1D] px-3 py-2 text-[11px] tracking-overline uppercase font-medium transition-colors"
        title="Edit"
      >
        <FileEdit className="w-4 h-4" strokeWidth={1.5} />
      </Link>
      <button
        onClick={() => onSend(q)}
        disabled={sendingId === q.id || q.status === "accepted" || q.status === "declined"}
        data-testid={`send-btn-${q.id}`}
        className="inline-flex items-center gap-1.5 text-[#2F2E2E] hover:text-[#A9927D] px-3 py-2 text-[11px] tracking-overline uppercase font-medium transition-colors disabled:opacity-30"
        title="Send via Brevo"
      >
        {sendingId === q.id ? (
          <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
        ) : (
          <Send className="w-4 h-4" strokeWidth={1.5} />
        )}
      </button>
      <button
        onClick={() => onDelete(q)}
        data-testid={`delete-btn-${q.id}`}
        className="inline-flex items-center gap-1.5 text-[#8A3D3D]/70 hover:text-[#8A3D3D] px-3 py-2 text-[11px] tracking-overline uppercase font-medium transition-colors"
        title="Delete"
      >
        <Trash2 className="w-4 h-4" strokeWidth={1.5} />
      </button>
    </div>
  );
}

export function QuoteRow({ q, stats, sendingId, onSend, onDelete, onAnalytics, onReactivate, onWhatsApp }) {
  return (
    <tr
      className="border-b border-[#F3F1ED] hover:bg-[#FAF9F6] transition-colors"
      data-testid={`quote-row-${q.id}`}
    >
      <td className="px-6 py-5">
        <div className="font-display text-xl text-[#1F1E1D] leading-none">
          {q.partner1_name} <span className="text-[#A9927D]">&</span> {q.partner2_name}
        </div>
        <div className="text-xs text-[#8A8782] mt-1 tabular-nums">
          {q.packages?.filter((p) => p.selected).length || 0} packages
        </div>
      </td>
      <td className="px-6 py-5 text-sm text-[#5C5A56]">{q.client_email}</td>
      <td className="px-6 py-5">
        <StatusBadge status={isQuoteExpired(q) ? "expired" : q.status} />
        {!isQuoteExpired(q) && <ExpiryLabel q={q} />}
        {q.reactivation_requested && (
          <div
            data-testid={`reactivation-flag-${q.id}`}
            className="mt-1.5 text-[10px] tracking-overline uppercase text-[#CCBA56] font-semibold"
          >
            Reactivation requested
          </div>
        )}
      </td>
      <td className="px-6 py-5" data-testid={`engagement-${q.id}`}>
        <EngagementCell q={q} stats={stats} onAnalytics={onAnalytics} />
      </td>
      <td className="px-6 py-5 text-xs text-[#8A8782] tabular-nums">
        {new Date(q.created_at).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })}
      </td>
      <td className="px-6 py-5">
        <RowActions
          q={q}
          sendingId={sendingId}
          onSend={onSend}
          onDelete={onDelete}
          onAnalytics={onAnalytics}
          onReactivate={onReactivate}
          onWhatsApp={onWhatsApp}
        />
      </td>
    </tr>
  );
}

